import {
  defaultRangeExtractor,
  type Range,
  useVirtualizer,
  type VirtualItem,
} from '@tanstack/react-virtual'
import {
  type CSSProperties,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { useScrollRestorationBase } from '#src/hooks/useScrollRestorationBase.ts'
import { mergeRefs } from '#src/utils/mergeRefs.ts'

/*
 * VirtualizedList supports two rendering modes:
 *
 * 1. rows[] mode (legacy): pass a pre-built ReactNode array. All nodes are
 *    instantiated before the virtualizer renders them — hooks fire for every
 *    item regardless of visibility.
 *
 * 2. renderItem mode (preferred): pass renderItem + itemCount. The function is
 *    called only for visible items, so hooks in row components fire lazily as
 *    items scroll into view. This is the high-performance path.
 *
 * Both modes are fully supported. Prefer renderItem for large lists.
 */
type VirtualizedListBaseProps = {
  estimateSize: (index: number) => number
  scrollRestorationId: string | null
  overscan?: number
  scrollElementClassName?: string
  scrollElementRef?: Ref<HTMLDivElement | null>
  dynamic?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  scrollToIndex?: number
  rangeExtractor?: (range: Range) => number[]
  getItemStyle?: (virtualItem: VirtualItem) => React.CSSProperties | undefined
  isGroupHeader?: (index: number) => boolean
  initialScrollToIndex?: number
}

type VirtualizedListRowsProps = VirtualizedListBaseProps & {
  /** Pre-built ReactNode array. All items instantiated eagerly. */
  rows: ReactNode[]
  renderItem?: never
  itemCount?: never
}

type VirtualizedListRenderItemProps = VirtualizedListBaseProps & {
  rows?: never
  /** Lazy render function — called only for visible items. */
  renderItem: (index: number) => ReactNode
  /** Total number of items in the list. */
  itemCount: number
}

export type VirtualizedListProps =
  | VirtualizedListRowsProps
  | VirtualizedListRenderItemProps

const STICKY_STYLE: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
}

export function VirtualizedList({
  rows,
  renderItem,
  itemCount: itemCountProp,
  estimateSize,
  overscan = 5,
  scrollElementClassName,
  scrollElementRef,
  scrollRestorationId,
  dynamic = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  scrollToIndex,
  rangeExtractor: rangeExtractorProp,
  getItemStyle: getItemStyleProp,
  isGroupHeader,
  initialScrollToIndex,
}: VirtualizedListProps): ReactNode {
  /* Unified item count regardless of rendering mode */
  const itemCount = renderItem ? (itemCountProp ?? 0) : (rows?.length ?? 0)
  const innerScrollElementRef = useRef<HTMLDivElement>(null)
  const activeStickyIndexRef = useRef<number | undefined>(undefined)
  /* Whether the active sticky header falls inside the virtualizer's own
     range, or was pulled in from above it only so it can stay pinned.
     Dynamic mode stacks rows in normal flow inside a translated wrapper, so
     the pinned header is always rendered outside that stack: inside it,
     `position: sticky` resolves against the untransformed layout and the
     header gets clamped to the stack's bottom edge, below the viewport.
     A header from above the range is dropped from the stack entirely (a
     stray row at the top would misposition every row below it and make
     Chrome's scroll anchoring fight the virtualizer); a header inside the
     range leaves a same-height spacer so the stack stays contiguous. */
  const activeStickyInRangeRef = useRef(false)

  /* Bypass scroll restoration when initialScrollToIndex is set so they
     don't fight. */
  const restoredOffset = useVirtualizedScrollRestoration(scrollRestorationId)
  const initialOffset =
    initialScrollToIndex !== undefined ? undefined : restoredOffset

  const stickyIndexes = useMemo(() => {
    if (!isGroupHeader) {
      return []
    }
    const indexes: number[] = []
    for (let i = 0; i < itemCount; i++) {
      if (isGroupHeader(i)) {
        indexes.push(i)
      }
    }
    return indexes
  }, [itemCount, isGroupHeader])

  const isSticky = useCallback(
    (index: number) => stickyIndexes.includes(index),
    [stickyIndexes]
  )

  const isActiveSticky = useCallback(
    (index: number) => activeStickyIndexRef.current === index,
    []
  )

  const rangeExtractor = useCallback(
    (range: Range) => {
      const baseIndexes = rangeExtractorProp
        ? rangeExtractorProp(range)
        : defaultRangeExtractor(range)

      if (!isGroupHeader) {
        activeStickyIndexRef.current = undefined
        activeStickyInRangeRef.current = false
        return baseIndexes
      }

      /* Only a header at or above the first visible row can be pinned. With
         no group headers at all there is nothing to pin, and falling back
         to index 0 here would keep the first row permanently rendered. */
      const activeStickyIndex = [...stickyIndexes]
        .reverse()
        .find((index) => range.startIndex >= index)
      activeStickyIndexRef.current = activeStickyIndex

      if (activeStickyIndex === undefined) {
        activeStickyInRangeRef.current = false
        return baseIndexes
      }

      activeStickyInRangeRef.current = baseIndexes.includes(activeStickyIndex)

      const next = new Set([activeStickyIndex, ...baseIndexes])

      return [...next].sort((a, b) => a - b)
    },
    [stickyIndexes, isGroupHeader, rangeExtractorProp]
  )

  const getItemStyle = useCallback(
    (virtualItem: VirtualItem): CSSProperties | undefined => {
      if (getItemStyleProp) {
        return getItemStyleProp(virtualItem)
      }

      if (!isGroupHeader) {
        return undefined
      }

      const baseStyle: CSSProperties = {
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualItem.size}px`,
      }

      if (isSticky(virtualItem.index)) {
        if (isActiveSticky(virtualItem.index)) {
          return {
            ...baseStyle,
            position: 'sticky',
            zIndex: 1,
          }
        }
        return {
          ...baseStyle,
          position: 'absolute',
          transform: `translateY(${virtualItem.start}px)`,
          zIndex: 1,
        }
      }

      return {
        ...baseStyle,
        position: 'absolute',
        transform: `translateY(${virtualItem.start}px)`,
      }
    },
    [getItemStyleProp, isGroupHeader, isSticky, isActiveSticky]
  )

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? itemCount + 1 : itemCount,
    getScrollElement: () => innerScrollElementRef.current,
    estimateSize,
    overscan,
    initialOffset,
    rangeExtractor,
  })

  const [highlightScrollBar, setHighlightScrollBar] = useState(false)
  useEffect(() => {
    if (!initialOffset) {
      return
    }
    setHighlightScrollBar(true)
    const scrollBarTimeoutId = setTimeout(() => {
      setHighlightScrollBar(false)
    }, 1000)
    return () => clearTimeout(scrollBarTimeoutId)
  }, [initialOffset])

  const items = rowVirtualizer.getVirtualItems()

  const getRow = (virtualItem: VirtualItem): ReactNode => {
    if (virtualItem.index > itemCount - 1) {
      return null
    }
    return renderItem
      ? renderItem(virtualItem.index)
      : rows?.[virtualItem.index]
  }

  /* Dynamic mode renders the contiguous range as a flow stack and the
     pinned header on its own, outside the stack. */
  const activeStickyIndex = activeStickyIndexRef.current
  const stickyItem =
    dynamic && activeStickyIndex !== undefined
      ? items.find((item) => item.index === activeStickyIndex)
      : undefined
  const stackItems =
    stickyItem && !activeStickyInRangeRef.current
      ? items.filter((item) => item !== stickyItem)
      : items

  // Infinite scroll: trigger onLoadMore when scrolling near the end
  // Based on official TanStack Virtual infinite scroll pattern:
  // https://tanstack.com/virtual/latest/docs/framework/react/examples/infinite-scroll
  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) {
      return
    }

    const lastItem = items[items.length - 1]
    if (!lastItem) {
      return
    }

    // Trigger when viewing the last actual data item or the loader row
    // We check >= itemCount - 1 (not subtracting overscan) because:
    // - overscan is already handled by the virtualizer for rendering
    // - we want to trigger only when user scrolls to see the last item
    if (itemCount > 0 && lastItem.index >= itemCount - 1) {
      onLoadMore()
    }
  }, [items, itemCount, onLoadMore, hasMore, isLoadingMore])

  // Keyboard navigation: scroll to selected item
  // https://tanstack.com/virtual/latest/docs/api/virtualizer#scrolltoindex
  useEffect(() => {
    if (
      scrollToIndex !== undefined &&
      scrollToIndex >= 0 &&
      scrollToIndex < itemCount
    ) {
      rowVirtualizer.scrollToIndex(scrollToIndex, {
        align: 'auto', // 'auto' only scrolls if item is not visible
        behavior: 'smooth',
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- don't change scroll when loading more items
  }, [scrollToIndex, rowVirtualizer])

  /* Instant snap to bring the target row into the DOM. Re-arms when the
     prop returns to undefined, which happens naturally when navigating to
     a different deep-link target (useRead loading state clears the upstream
     index). This prevents unwanted re-scrolls from background data refreshes
     that merely shift row indices. */
  const initialScrollFired = useRef(false)
  useEffect(() => {
    if (initialScrollToIndex === undefined || initialScrollToIndex < 0) {
      initialScrollFired.current = false
      return
    }
    if (initialScrollFired.current) {
      return
    }
    initialScrollFired.current = true
    rowVirtualizer.scrollToIndex(initialScrollToIndex, {
      align: 'center',
      behavior: 'auto',
    })
  }, [initialScrollToIndex, rowVirtualizer])

  return (
    <ScrollArea
      scrollElementRef={mergeRefs([innerScrollElementRef, scrollElementRef])}
      contentClassName={scrollElementClassName}
      scrollRestorationId={scrollRestorationId}
      highlightScrollBar={highlightScrollBar}
      disableScrollRestoration // useVirtualizer handles scroll restoration. We still pass scrollRestorationId so that we can save & load the scroll position in the useVirtualizedScrollRestoration hook below.
    >
      <div
        className="relative w-full"
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        {!dynamic &&
          items.map((virtualItem) => {
            const customStyle = getItemStyle?.(virtualItem)

            const defaultStyle: React.CSSProperties = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            }

            return (
              <div key={virtualItem.key} style={customStyle ?? defaultStyle}>
                {getRow(virtualItem)}
              </div>
            )
          })}
        {dynamic && stickyItem && (
          <div
            key={stickyItem.key}
            data-index={stickyItem.index}
            ref={rowVirtualizer.measureElement}
            style={STICKY_STYLE}
          >
            {getRow(stickyItem)}
          </div>
        )}
        {dynamic && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${stackItems[0]?.start ?? 0}px)`,
            }}
          >
            {stackItems.map((virtualItem) =>
              virtualItem === stickyItem ? (
                <div
                  key={virtualItem.key}
                  data-sticky-spacer-index={virtualItem.index}
                  style={{ height: `${virtualItem.size}px` }}
                />
              ) : (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                >
                  {getRow(virtualItem)}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </ScrollArea>
  )
}

function useVirtualizedScrollRestoration(
  id: string | undefined | null
): number | undefined {
  const { storage, locationKey } = useScrollRestorationBase(id)

  const cachedPosition = useRef<number | undefined>(undefined)

  if (!id) {
    return undefined
  }

  if (cachedPosition.current === undefined && storage) {
    cachedPosition.current = storage.get()?.[locationKey]
  }
  return cachedPosition.current
}
